/**
 * useNotifications Hook
 * Manages user notifications including fetching, marking as read, and filtering
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { apiRequest } from '../Api';

export const useNotifications = (autoRefresh = true, refreshInterval = 10000) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async (limit = 20) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiRequest(`/api/notification?limit=${limit}`);
      setNotifications(response.notifications || []);
      setUnreadCount(response.unreadCount || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch unread count only (lightweight endpoint)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiRequest('/api/notification/count');
      setUnreadCount(response.unreadCount || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
      // Don't set error for this lightweight call
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await apiRequest(`/api/notification/${notificationId}/read`, {
        method: 'PATCH',
      });

      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId ? { ...n, isRead: true } : n
        )
      );

      // Update unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError(err);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await apiRequest('/api/notification/read-all', {
        method: 'PATCH',
      });

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError(err);
    }
  }, []);

  // Filter notifications by type
  const getNotificationsByType = useCallback(
    (type) => {
      return notifications.filter((n) => n.type === type);
    },
    [notifications]
  );

  // Get unread notifications only
  const getUnreadNotifications = useCallback(() => {
    return notifications.filter((n) => !n.isRead);
  }, [notifications]);

  // Start auto-refresh
  const startAutoRefresh = useCallback(() => {
    if (autoRefresh && !refreshIntervalRef.current) {
      // Initial fetch
      fetchUnreadCount();

      // Set up interval to check unread count (lightweight)
      refreshIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
      }, refreshInterval);
    }
  }, [autoRefresh, refreshInterval, fetchUnreadCount]);

  // Stop auto-refresh
  const stopAutoRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Initialize auto-refresh on mount
  useEffect(() => {
    if (autoRefresh) {
      startAutoRefresh();
    }

    return () => {
      stopAutoRefresh();
    };
  }, [autoRefresh, startAutoRefresh, stopAutoRefresh]);

  // Auto-fetch notifications when unread count increases
  useEffect(() => {
    if (unreadCount > previousUnreadCount) {
      // New notification arrived - fetch the full list
      fetchNotifications();
      setPreviousUnreadCount(unreadCount);
    }
  }, [unreadCount, previousUnreadCount, fetchNotifications]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoRefresh();
    };
  }, [stopAutoRefresh]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    getNotificationsByType,
    getUnreadNotifications,
    startAutoRefresh,
    stopAutoRefresh,
  };
};

export default useNotifications;
